import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  FolderTree,
  Tag,
  ShoppingBag,
  Package,
  CreditCard,
  Hammer,
  Truck,
  Sliders,
  Megaphone,
  Star,
  MessageSquare,
  Settings,
  X
} from 'lucide-react';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const menuItems = [
    {
      icon: LayoutDashboard,
      labelKey: 'menu.dashboard',
      path: '/',
      type: 'single'
    },
    {
      icon: ShoppingBag,
      labelKey: 'menu.products',
      path: '/products',
      type: 'single'
    },
    {
      icon: FolderTree,
      labelKey: 'menu.categories',
      path: '/categories',
      type: 'single'
    },
    {
      icon: Tag,
      labelKey: 'menu.brands',
      path: '/brands',
      type: 'single'
    },
    {
      icon: Megaphone,
      labelKey: 'menu.campaigns',
      path: '/campaigns',
      type: 'single'
    },
    {
      icon: Star,
      labelKey: 'menu.boutiquePromotions',
      path: '/boutique-promotions',
      type: 'single'
    },
    {
      icon: Sliders,
      labelKey: 'menu.attributes',
      path: '/attributes',
      type: 'single'
    },
    {
      icon: Package,
      labelKey: 'menu.orders',
      path: '/orders',
      type: 'single'
    },
    {
      icon: Truck,
      labelKey: 'menu.shippingZones',
      path: '/shipping-zones',
      type: 'single'
    },
    {
      icon: Hammer,
      labelKey: 'menu.offers',
      path: '/offers',
      type: 'single'
    },
    {
      icon: CreditCard,
      labelKey: 'menu.payments',
      path: '/payments',
      type: 'single'
    },
    {
      icon: Users,
      labelKey: 'menu.users',
      path: '/users',
      type: 'single'
    },
    {
      icon: MessageSquare,
      labelKey: 'menu.messaging',
      path: '/messaging',
      type: 'single'
    },
    {
      icon: Settings,
      labelKey: 'menu.settings',
      path: '/settings',
      type: 'single'
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="bg-gray-800 w-64 shadow-lg flex flex-col h-screen">
      {/* Logo with close button for mobile */}
      <div className="p-4 md:p-6 border-b border-gray-700 flex items-center justify-between">
        <h1 className="text-xl md:text-2xl font-bold text-white">VOG Admin</h1>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden text-gray-300 hover:text-white p-1"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav-scroll flex-1 p-2 md:p-4 overflow-y-auto">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isMenuActive = isActive(item.path);

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => onClose?.()}
                  className={`flex items-center space-x-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors duration-200 ${
                    isMenuActive
                      ? 'bg-custom-accent-light text-custom-charcoal border border-custom-secondary/30'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Icon size={18} className="md:w-5 md:h-5" />
                  <span className="font-medium text-sm md:text-base">{t(item.labelKey, { ns: 'navigation' })}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

    </div>
  );
};

export default Sidebar;